import {
  AnyAction,
  combineReducers,
  configureStore,
  createListenerMiddleware,
  Reducer,
  UnknownAction,
} from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage' // defaults to localStorage for web
import authReducer, { initialState as authInitialState } from './authSlice'
import songsReducer, { initialState as songsInitialState } from './songsSlice'
import usersReducer, { initialState as usersInitialState } from './usersSlice'
import playlistsReducer, { initialState as playlistsInitialState } from './playlistsSlice'
import moodsReducer, { initialState as moodsInitialState } from './moodsSlice'
import businessesReducer, { initialState as businessesInitialState } from './businessesSlice'
import daytimesReducer, { initialState as daytimesInitialState } from './daytimesSlice'
import stylesReducer, { initialState as stylesInitialState } from './stylesSlice'
import appReducer, { initialState as appInitialState } from './appSlice'
import ordersReducer, { initialState as ordersInitialState } from './ordersSlice'
import statisticsReducer, { initialState as statisticsInitialState } from './statisticsSlice'

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'app'],
}

const combinedReducer = combineReducers({
  auth: authReducer,
  app: appReducer,
  user: usersReducer,
  songs: songsReducer,
  playlists: playlistsReducer,
  moods: moodsReducer,
  businesses: businessesReducer,
  daytimes: daytimesReducer,
  styles: stylesReducer,
  orders: ordersReducer,
  statistics: statisticsReducer,
})

const initialState = {
  auth: authInitialState,
  app: appInitialState,
  user: usersInitialState,
  songs: songsInitialState,
  playlistsReducer: playlistsInitialState,
  moodsReducer: moodsInitialState,
  businessesReducer: businessesInitialState,
  daytimesReducer: daytimesInitialState,
  stylesReducer: stylesInitialState,
  ordersReducer: ordersInitialState,
  statisticsReducer: statisticsInitialState,
}
export const rootReducer: Reducer = (state: RootState, action: UnknownAction) => {
  if (action.type === 'auth/logout') {
    return combinedReducer({ ...initialState /* app: state.app */ }, action)
  }
  return combinedReducer(state, action)
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

const listenerMiddleware = createListenerMiddleware()

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }).prepend(listenerMiddleware.middleware),
})

const persistor = persistStore(store)
const useAppDispatch: () => AppDispatch = useDispatch
const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

type RootState = ReturnType<typeof combinedReducer>
type AppDispatch = typeof store.dispatch

export {
  listenerMiddleware,
  store,
  persistor,
  useAppDispatch,
  useAppSelector,
  type RootState,
  type AppDispatch,
}
